import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { DEFAULT_LOGO, getClubLogo } from '../clubLogos'
import Logo from '../components/Logo'

function formatDate(dateStr) {
  if (!dateStr) return { month: '—', day: '—', time: '', full: '' }
  const d = new Date(dateStr)
  return {
    month: d.toLocaleString('en-IE', { month: 'short', timeZone: 'UTC' }),
    day: d.getUTCDate(),
    time: d.toLocaleString('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }),
    full: d.toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
}

const ACTIVE_PHASES = ['playing', 'ht', 'et1', 'et_ht', 'et2']
function isMatchLive(f) {
  return f.clock_running || ACTIVE_PHASES.includes(f.match_phase)
}
function phaseShortLabel(f) {
  if (f.match_phase === 'ht') return 'HT'
  if (f.match_phase === 'et_ht') return 'ET HT'
  return null
}

const tabBtn = (active) => ({
  flex: 1, padding: '10px 4px', background: 'none', border: 'none', cursor: 'pointer',
  fontSize: '11px', fontWeight: '500',
  color: active ? '#185FA5' : '#888',
  borderBottom: active ? '2px solid #185FA5' : '2px solid transparent'
})

const filterBtn = (active) => ({
  padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
  fontSize: '11px', fontWeight: '500',
  background: active ? '#185FA5' : '#e8e8e8',
  color: active ? 'white' : '#555'
})

export default function PublicClub() {
  const slug = window.location.pathname.replace('/public/club/', '').replace(/\/$/, '')
  const [club, setClub] = useState(null)
  const [squads, setSquads] = useState([])
  const [fixtures, setFixtures] = useState([])
  const [liveFixtures, setLiveFixtures] = useState([])
  const [liveFixture, setLiveFixture] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [activeTab, setActiveTab] = useState('fixtures')
  const [squadFilter, setSquadFilter] = useState('all')
  const [sportFilter, setSportFilter] = useState('all')

  useEffect(() => { loadData() }, [])

  useEffect(() => {
    if (liveFixtures.length === 0) return
    const interval = setInterval(() => loadLiveFixtures(), 15000)
    return () => clearInterval(interval)
  }, [liveFixtures.length])

  async function loadLiveFixtures() {
    if (!club) return
    const { data } = await supabase.from('fixtures')
      .select('*').eq('club_id', club.id).eq('status', 'live')
    if (data) setLiveFixtures(data)
  }

  async function loadData() {
    const { data: clubData } = await supabase
      .from('clubs').select('*').eq('slug', slug).maybeSingle()
    if (!clubData) { setNotFound(true); setLoading(false); return }
    setClub(clubData)

    const { data: squadsData } = await supabase
      .from('squads').select('*').eq('club_id', clubData.id).order('name')
    setSquads(squadsData || [])

    const { data: fixturesData } = await supabase
      .from('fixtures').select('*, squads(name, slug)')
      .eq('club_id', clubData.id)
      .order('fixture_date', { ascending: false })
    setFixtures(fixturesData || [])

    const live = (fixturesData || []).filter(f => f.status === 'live')
    setLiveFixtures(live)
    if (live.length > 0) setLiveFixture(live[0])
    setLoading(false)
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: 'white', fontSize: '16px' }}>Loading...</p>
    </div>
  )

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', textAlign: 'center', maxWidth: '340px' }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🏐</p>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Club not found</h2>
        <p style={{ fontSize: '14px', color: '#888' }}>This link may be invalid or the club has been removed.</p>
      </div>
    </div>
  )

  const clubLogo = club ? getClubLogo(club.club_name) : DEFAULT_LOGO

  const hasMultipleSquads = squads.length > 1
  const hasHurling = fixtures.some(f => f.sport === 'hurling')
  const hasFootball = fixtures.some(f => f.sport === 'football')

  const applyFilters = (list) => list.filter(f => {
    if (squadFilter !== 'all' && f.squad_id !== squadFilter) return false
    if (sportFilter !== 'all' && f.sport !== sportFilter) return false
    return true
  })

  const allUpcoming = applyFilters(
    fixtures.filter(f => f.status === 'upcoming' || f.status === 'live')
  ).sort((a, b) => new Date(a.fixture_date) - new Date(b.fixture_date))

  const allCompleted = applyFilters(
    fixtures.filter(f => f.status === 'completed')
  ).sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))

  const sportLabel = (s) => s === 'hurling' ? '🥍 Hurling' : s === 'camogie' ? '🥍 Camogie' : '⚽ Football'
  const teamLabel = (n) => 'Team ' + (n || 1)

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ background: '#185FA5', color: 'white', padding: '16px', textAlign: 'center' }}>
        <Logo variant="light" width="180px" maxWidth="180px" style={{ margin: '0 auto 12px' }} />
        <img src={clubLogo} alt="" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'contain', marginBottom: '8px', background: 'rgba(255,255,255,0.1)' }} onError={e => { e.target.style.display = 'none' }} />
        <h1 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 2px' }}>{club.club_name}</h1>
        {club.county && <p style={{ fontSize: '12px', opacity: 0.7, margin: '0 0 6px' }}>{club.county} GAA</p>}
        <p style={{ fontSize: '10px', opacity: 0.5, margin: 0 }}>🔗 gaamatchtracker.ie/public/club/{slug}</p>
      </div>

      {/* Squads overview strip */}
      {squads.length > 0 && (
        <div style={{ background: '#0d3d6b', padding: '10px 16px', display: 'flex', gap: '8px', overflowX: 'auto' }}>
          {squads.map(s => (
            <a key={s.id} href={`/public/${s.slug}`}
              style={{ flexShrink: 0, padding: '5px 12px', borderRadius: '20px', background: 'rgba(255,255,255,0.12)', color: 'white', fontSize: '11px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              {s.name} →
            </a>
          ))}
        </div>
      )}

      {/* Live banners */}
      {liveFixtures.filter(isMatchLive).length > 0 && activeTab !== 'live' && liveFixtures.filter(isMatchLive).map(f => (
        <div key={f.id} onClick={() => { setLiveFixture(f); setActiveTab('live') }}
          style={{ background: '#A32D2D', color: 'white', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />
            <div>
              <span style={{ fontSize: '12px', fontWeight: '500' }}>
                {phaseShortLabel(f) ? phaseShortLabel(f) + ' vs ' + f.opponent : 'LIVE vs ' + f.opponent}
              </span>
              <span style={{ fontSize: '10px', opacity: 0.8, marginLeft: '6px' }}>
                {f.squads?.name} · {f.sport === 'hurling' ? 'Hurling' : 'Football'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700' }}>
              {f.home_goals}-{String(f.home_points).padStart(2, '0')} · {f.away_goals}-{String(f.away_points).padStart(2, '0')}
            </span>
            <span style={{ fontSize: '11px', opacity: 0.9 }}>Watch →</span>
          </div>
        </div>
      ))}

      {/* Tabs */}
      {activeTab !== 'live' && (
        <div style={{ display: 'flex', background: 'white', borderBottom: '0.5px solid #e0e0e0' }}>
          {[
            { id: 'fixtures', label: '📅 Fixtures' },
            { id: 'results', label: '🏆 Results' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabBtn(activeTab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      {activeTab !== 'live' && (hasMultipleSquads || (hasHurling && hasFootball)) && (
        <div style={{ background: 'white', padding: '8px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {hasMultipleSquads && (
            <>
              <button style={filterBtn(squadFilter === 'all')} onClick={() => setSquadFilter('all')}>All Squads</button>
              {squads.map(s => (
                <button key={s.id} style={filterBtn(squadFilter === s.id)} onClick={() => setSquadFilter(s.id)}>{s.name}</button>
              ))}
            </>
          )}
          {hasHurling && hasFootball && (
            <>
              <button style={filterBtn(sportFilter === 'all')} onClick={() => setSportFilter('all')}>All Sports</button>
              <button style={filterBtn(sportFilter === 'football')} onClick={() => setSportFilter('football')}>Football</button>
              <button style={filterBtn(sportFilter === 'hurling')} onClick={() => setSportFilter('hurling')}>Hurling</button>
            </>
          )}
        </div>
      )}

      {/* Fixtures tab */}
      {activeTab === 'fixtures' && (
        <div style={{ padding: '12px 16px' }}>
          {allUpcoming.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '32px', fontSize: '14px' }}>No upcoming fixtures</p>
          ) : allUpcoming.map(f => {
            const date = formatDate(f.fixture_date)
            const oppLogo = getClubLogo(f.opponent)
            const activePhases = ['playing', 'ht', 'et1', 'et_ht', 'et2']
            const isLive = f.status === 'live' && (f.clock_running || activePhases.includes(f.match_phase))
            const phaseLabel = f.match_phase === 'ht' ? '🔴 HT' : f.match_phase === 'et_ht' ? '🔴 ET HT' : '🔴 LIVE'
            return (
              <div key={f.id} style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ textAlign: 'center', minWidth: '32px' }}>
                    <p style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>{date.month}</p>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1 }}>{date.day}</p>
                  </div>
                  <img src={oppLogo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '13px', fontWeight: '500' }}>vs {f.opponent}</p>
                    <p style={{ fontSize: '11px', color: '#888' }}>{date.time}{f.venue ? ' · ' + f.venue : ''}</p>
                    <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
                        {f.squads?.name}
                      </span>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
                        {sportLabel(f.sport)}
                      </span>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
                        {teamLabel(f.team_number)}
                      </span>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
                        {f.game_type === 'league' ? 'League' : f.game_type === 'championship' ? 'Championship' : f.game_type === 'cup' ? 'Cup' : 'Friendly'}
                      </span>
                      {isLive && (
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#FF3B30', color: 'white', fontWeight: '500' }}>
                          {phaseLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  {f.venue_url && (
                    <a href={f.venue_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '11px', padding: '5px 8px', borderRadius: '8px', background: '#E6F1FB', color: '#185FA5', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      📍 Map
                    </a>
                  )}
                </div>
                {isLive && (
                  <button onClick={() => { setLiveFixture(f); setActiveTab('live') }}
                    style={{ display: 'block', width: '100%', marginTop: '10px', padding: '8px', background: '#A32D2D', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}>
                    Watch live →
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Results tab */}
      {activeTab === 'results' && (
        <div style={{ padding: '12px 16px' }}>
          {allCompleted.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '32px', fontSize: '14px' }}>No results yet</p>
          ) : allCompleted.map(f => {
            const date = formatDate(f.fixture_date)
            const oppLogo = getClubLogo(f.opponent)
            const ours = f.home_goals * 3 + f.home_points
            const theirs = f.away_goals * 3 + f.away_points
            const result = ours > theirs ? 'W' : ours < theirs ? 'L' : 'D'
            const resultColor = result === 'W' ? '#3B6D11' : result === 'L' ? '#A32D2D' : '#888'
            const resultBg = result === 'W' ? '#EAF3DE' : result === 'L' ? '#FCEBEB' : '#f8f8f8'
            return (
              <ClubResultCard key={f.id} fixture={f} date={date} oppLogo={oppLogo}
                ours={ours} theirs={theirs} result={result}
                resultColor={resultColor} resultBg={resultBg}
                sportLabel={sportLabel} teamLabel={teamLabel} />
            )
          })}
        </div>
      )}

      {/* Scorers tab removed */}

      {/* Live view */}
      {activeTab === 'live' && liveFixture && (
        <ClubLiveView fixture={liveFixture} onBack={() => setActiveTab('fixtures')} club={club} squads={squads} />
      )}

      {/* Footer */}
      <div style={{ padding: '20px', textAlign: 'center', borderTop: '0.5px solid #e0e0e0', marginTop: '20px' }}>
        <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>Powered by</p>
        <a href="https://gaamatchtracker.ie" style={{ fontSize: '12px', color: '#185FA5', fontWeight: '500', display: 'block', marginBottom: '4px' }}>GAA Match Tracker</a>
        <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px' }}>Track your team's matches, fixtures and results</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <a href="https://gaamatchtracker.ie" style={{ fontSize: '11px', color: '#185FA5' }}>Sign in</a>
          <a href="https://gaamatchtracker.ie/login?mode=signup" style={{ fontSize: '11px', color: '#185FA5' }}>Register your club</a>
        </div>
      </div>
    </div>
  )
}

function ClubResultCard({ fixture: f, date, oppLogo, ours, theirs, result, resultColor, resultBg, sportLabel, teamLabel }) {
  const [scorers, setScorers] = useState([])
  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('match_events')
        .select('player_id, event_type, players(full_name)')
        .eq('fixture_id', f.id)
        .in('event_type', ['goal', 'point', 'twopoint'])
      if (!data) return
      const tally = {}
      data.forEach(e => {
        const key = e.player_id || 'unknown'
        const name = e.players?.full_name || 'Unknown'
        if (!tally[key]) tally[key] = { name, goals: 0, points: 0 }
        if (e.event_type === 'goal') tally[key].goals++
        if (e.event_type === 'point') tally[key].points++
        if (e.event_type === 'twopoint') tally[key].points += 2
      })
      setScorers(Object.values(tally).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points)))
    }
    load()
  }, [f.id])
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ textAlign: 'center', minWidth: '32px' }}>
          <p style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>{date.month}</p>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1 }}>{date.day}</p>
        </div>
        <img src={oppLogo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '13px', fontWeight: '500' }}>vs {f.opponent}</p>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
            {f.home_goals}-{String(f.home_points).padStart(2, '0')} · {f.away_goals}-{String(f.away_points).padStart(2, '0')}
          </p>
          <p style={{ fontSize: '11px', color: '#999' }}>({ours} pts) vs ({theirs} pts)</p>
          <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
              {f.squads?.name}
            </span>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
              {sportLabel(f.sport)}
            </span>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
              {teamLabel(f.team_number)}
            </span>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
              {f.game_type === 'league' ? 'League' : f.game_type === 'championship' ? 'Championship' : f.game_type === 'cup' ? 'Cup' : 'Friendly'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', background: resultBg, color: resultColor }}>
            {result}
          </span>
          {f.venue_url && (
            <a href={f.venue_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '8px', background: '#E6F1FB', color: '#185FA5', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              📍 Map
            </a>
          )}
        </div>
      </div>
      {scorers.length > 0 && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '0.5px solid #f0f0f0' }}>
          <p style={{ fontSize: '10px', color: '#aaa', marginBottom: '4px', fontWeight: '600' }}>SCORERS</p>
          <p style={{ fontSize: '12px', color: '#555' }}>
            {scorers.map(s => s.name + ' ' + s.goals + '-' + String(s.points).padStart(2, '0')).join(' · ')}
          </p>
        </div>
      )}
    </div>
  )
}

function ClubTopScorers({ clubId, fixtures, squads }) {
  const [scorers, setScorers] = useState([])
  const [loading, setLoading] = useState(true)
  const [squadFilter, setSquadFilter] = useState('all')

  useEffect(() => {
    if (!clubId) return
    loadScorers()
  }, [clubId])

  async function loadScorers() {
    const completedIds = fixtures.filter(f => f.status === 'completed').map(f => f.id)
    if (completedIds.length === 0) { setLoading(false); return }
    const { data } = await supabase.from('match_events')
      .select('player_id, event_type, fixture_id, players(full_name)')
      .in('fixture_id', completedIds)
      .in('event_type', ['goal', 'point', 'twopoint'])
    const tally = {}
    ;(data || []).forEach(e => {
      const fixture = fixtures.find(f => f.id === e.fixture_id)
      const key = e.player_id + '-' + fixture?.squad_id
      const name = e.players?.full_name || 'Unknown'
      const squadId = fixture?.squad_id
      const squadName = squads.find(s => s.id === squadId)?.name || ''
      if (!tally[key]) tally[key] = { name, squadId, squadName, goals: 0, points: 0 }
      if (e.event_type === 'goal') tally[key].goals++
      if (e.event_type === 'point') tally[key].points++
      if (e.event_type === 'twopoint') tally[key].points += 2
    })
    const sorted = Object.values(tally).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points))
    setScorers(sorted)
    setLoading(false)
  }

  const hasMultipleSquads = squads.length > 1
  const filtered = squadFilter === 'all' ? scorers : scorers.filter(s => s.squadId === squadFilter)

  return (
    <div>
      {hasMultipleSquads && (
        <div style={{ background: 'white', padding: '8px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button style={filterBtn(squadFilter === 'all')} onClick={() => setSquadFilter('all')}>All Squads</button>
          {squads.map(s => (
            <button key={s.id} style={filterBtn(squadFilter === s.id)} onClick={() => setSquadFilter(s.id)}>{s.name}</button>
          ))}
        </div>
      )}
      <div style={{ padding: '12px 16px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', padding: '32px', color: '#888' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '32px', color: '#888', fontSize: '14px' }}>No scores recorded yet</p>
        ) : filtered.map((s, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '12px 16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <span style={{ fontSize: '15px', fontWeight: '700', minWidth: '24px', color: i === 0 ? '#854F0B' : i === 1 ? '#888' : i === 2 ? '#3B6D11' : '#bbb' }}>
              {i + 1}
            </span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>{s.name}</p>
              {hasMultipleSquads && <p style={{ fontSize: '11px', color: '#888', margin: '2px 0 0' }}>{s.squadName}</p>}
            </div>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#185FA5' }}>
              {s.goals}-{String(s.points).padStart(2, '0')}
            </span>
            <span style={{ fontSize: '11px', color: '#888' }}>({s.goals * 3 + s.points} pts)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ClubLiveView({ fixture, onBack, club, squads }) {
  const [liveData, setLiveData] = useState(fixture)
  const [events, setEvents] = useState([])
  const [seconds, setSeconds] = useState(0)
  const clockRef = useRef(null)
  const squadName = squads.find(s => s.id === fixture.squad_id)?.name || club.club_name

  useEffect(() => {
    loadEvents()
    syncClock()
    const poll = setInterval(() => { syncClock(); loadEvents() }, 10000)
    return () => { clearInterval(poll); if (clockRef.current) clearInterval(clockRef.current) }
  }, [])

  async function syncClock() {
    const { data } = await supabase.from('fixtures')
      .select('home_goals, home_points, away_goals, away_points, clock_started_at, clock_seconds_at_start, clock_running, clock_half, match_phase, status')
      .eq('id', fixture.id).single()
    if (!data) return
    setLiveData(data)
    if (clockRef.current) clearInterval(clockRef.current)
    if (data.clock_running && data.clock_started_at) {
      const startEpoch = new Date(data.clock_started_at).getTime()
      const base = data.clock_seconds_at_start || 0
      const tick = () => setSeconds(Math.min(base + Math.floor((Date.now() - startEpoch) / 1000), 3000))
      tick()
      clockRef.current = setInterval(tick, 1000)
    } else {
      setSeconds(data.clock_seconds_at_start || 0)
    }
  }

  async function loadEvents() {
    const { data } = await supabase.from('match_events')
      .select('*, players(full_name)')
      .eq('fixture_id', fixture.id)
      .in('event_type', ['goal', 'point', 'twopoint', 'wide', 'opp_wide'])
      .order('minute', { ascending: true })
    setEvents(data || [])
  }

  const totalHome = liveData.home_goals * 3 + liveData.home_points
  const totalAway = liveData.away_goals * 3 + liveData.away_points
  const diff = Math.abs(totalHome - totalAway)
  const leadText = totalHome > totalAway ? 'Leading by ' + diff + ' pt' + (diff !== 1 ? 's' : '')
    : totalAway > totalHome ? fixture.opponent + ' leading by ' + diff + ' pt' + (diff !== 1 ? 's' : '')
    : 'Level'
  const clockDisplay = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0')

  const scorerTallies = {}
  events.forEach(e => {
    if (!['goal', 'point', 'twopoint'].includes(e.event_type)) return
    const key = e.player_id || 'unknown'
    const name = e.players?.full_name || 'Unknown'
    if (!scorerTallies[key]) scorerTallies[key] = { name, goals: 0, points: 0 }
    if (e.event_type === 'goal') scorerTallies[key].goals++
    if (e.event_type === 'point') scorerTallies[key].points++
    if (e.event_type === 'twopoint') scorerTallies[key].points += 2
  })
  const scorerList = Object.values(scorerTallies).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points))

  const phaseDisplay = liveData.match_phase === 'ht' ? '⏸ Half Time' :
    liveData.match_phase === 'ft' ? '🏁 Full Time' :
    liveData.match_phase === 'et1' ? 'ET 1st Half' :
    liveData.match_phase === 'et_ht' ? '⏸ ET Half Time' :
    liveData.match_phase === 'et2' ? 'ET 2nd Half' :
    liveData.match_phase === 'et_ft' ? '🏁 ET Full Time' :
    (liveData.clock_half || 1) === 1 ? '1st Half' : '2nd Half'

  return (
    <div>
      <button onClick={onBack}
        style={{ display: 'block', width: '100%', padding: '10px 16px', background: '#f0f0f0', border: 'none', fontSize: '13px', color: '#555', cursor: 'pointer', textAlign: 'left' }}>
        ← Back to fixtures
      </button>
      <div style={{ background: '#0d3d6b', padding: '5px 16px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
        <span style={{ fontSize: '11px', color: '#B5D4F4', opacity: 0.8 }}>{squadName}</span>
        <span style={{ fontSize: '11px', color: '#B5D4F4', opacity: 0.4 }}>·</span>
        <span style={{ fontSize: '11px', color: '#B5D4F4', opacity: 0.8 }}>{fixture.sport === 'hurling' ? '🥍 Hurling' : '⚽ Football'}</span>
      </div>
      <div style={{ background: '#185FA5', color: 'white', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>{squadName}</p>
            <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '-1px', lineHeight: 1, margin: 0 }}>
              {liveData.home_goals}-{String(liveData.home_points).padStart(2, '0')}
            </p>
            <p style={{ fontSize: '11px', opacity: 0.7, margin: '2px 0 0' }}>({totalHome} pts)</p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '4px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />
              <p style={{ fontSize: '11px', opacity: 0.8, margin: 0 }}>{phaseDisplay}</p>
            </div>
            <p style={{ fontSize: '24px', fontWeight: '600', fontVariantNumeric: 'tabular-nums', margin: 0 }}>
              {['ht', 'ft', 'et_ht', 'et_ft'].includes(liveData.match_phase) ? '00:00' : clockDisplay}
            </p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>{fixture.opponent}</p>
            <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '-1px', lineHeight: 1, margin: 0 }}>
              {liveData.away_goals}-{String(liveData.away_points).padStart(2, '0')}
            </p>
            <p style={{ fontSize: '11px', opacity: 0.7, margin: '2px 0 0' }}>({totalAway} pts)</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '4px 12px' }}>
          {leadText}
        </div>
      </div>
      {scorerList.length > 0 && (
        <div style={{ background: 'white', margin: '12px 16px', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ fontSize: '11px', color: '#888', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '10px' }}>SCORERS</p>
          {scorerList.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
              <span style={{ fontSize: '13px', flex: 1 }}>{s.name}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#185FA5' }}>
                {s.goals}-{String(s.points).padStart(2, '00')}
              </span>
            </div>
          ))}
        </div>
      )}
      <p style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', padding: '8px' }}>Live · updates every 10s</p>
    </div>
  )
}